import type { FieldSensitivity } from '../../../core/fields/types/FieldManifest';
import type { RoleLevel } from '../../access/services/AccessControlService';
import type { FolderRecord } from '../models/FolderRecord';

export type RecordSensitivityTier = 'public' | 'internal' | 'restricted' | 'confidential';
export type ViewerTierCeiling = FieldSensitivity | RecordSensitivityTier | 'all';
export type RecordVisibilityDisposition = 'visible' | 'redact-fields' | 'hidden';

export const RECORD_SENSITIVITY_TIERS = [
  'public',
  'internal',
  'restricted',
  'confidential',
] as const satisfies readonly RecordSensitivityTier[];

export interface RecordVisibilityViewerContext {
  readonly viewerTierCeiling?: ViewerTierCeiling | null;
  readonly includeRetired?: boolean;
}

export interface RecordVisibilityDecision {
  readonly disposition: RecordVisibilityDisposition;
  readonly recordTier: FieldSensitivity;
  readonly viewerTierCeiling: FieldSensitivity | 'all';
}

export interface FolderRecordReadOptions extends RecordVisibilityViewerContext {
  readonly onVisibilityFilter?: (summary: RecordVisibilityFilterSummary) => void;
}

export interface RecordVisibilityFilterSummary {
  readonly total: number;
  readonly visible: number;
  readonly hidden: number;
  readonly redactedFields: number;
}

const FIELD_SENSITIVITY_ORDER: FieldSensitivity[] = [
  'public',
  'internal',
  'manager',
  'hr_confidential',
  'hr_restricted',
  'sensitive_pii',
];

const RECORD_TIER_TO_FIELD_SENSITIVITY: Record<RecordSensitivityTier, FieldSensitivity> = {
  public: 'public',
  internal: 'internal',
  restricted: 'hr_restricted',
  confidential: 'sensitive_pii',
};

const FIELD_SENSITIVITIES = new Set<FieldSensitivity>(FIELD_SENSITIVITY_ORDER);
const RECORD_TIERS = new Set<RecordSensitivityTier>(RECORD_SENSITIVITY_TIERS);

export function tierCeilingForFolderRole(role: RoleLevel): ViewerTierCeiling {
  switch (role) {
    case 'owner':
    case 'moderator':
    case 'editor':
      return 'all';
    case 'viewer':
      return 'internal';
    case 'none':
    default:
      return 'public';
  }
}

export function evaluateRecordVisibility(
  record: Pick<FolderRecord, 'universalProps'>,
  viewerCtx: RecordVisibilityViewerContext = {},
): RecordVisibilityDecision {
  if (!viewerCtx.includeRetired && isRetiredRecord(record.universalProps)) {
    return {
      disposition: 'hidden',
      recordTier: normalizeRecordSensitivity(record.universalProps),
      viewerTierCeiling: normalizeViewerTierCeiling(viewerCtx.viewerTierCeiling),
    };
  }
  const recordTier = normalizeRecordSensitivity(record.universalProps);
  const viewerTierCeiling = normalizeViewerTierCeiling(viewerCtx.viewerTierCeiling);
  if (
    viewerTierCeiling === 'all' ||
    sensitivityRank(recordTier) <= sensitivityRank(viewerTierCeiling)
  ) {
    return {
      disposition: 'visible',
      recordTier,
      viewerTierCeiling,
    };
  }

  return {
    disposition: 'hidden',
    recordTier,
    viewerTierCeiling,
  };
}

export function filterRecordsForViewer<T extends Pick<FolderRecord, 'universalProps'>>(
  records: readonly T[],
  viewerCtx: FolderRecordReadOptions = {},
): {
  records: T[];
  summary: RecordVisibilityFilterSummary;
  hiddenRecords: T[];
} {
  const visible: T[] = [];
  const hidden: T[] = [];
  let redactedFields = 0;

  for (const record of records) {
    const decision = evaluateRecordVisibility(record, viewerCtx);
    if (decision.disposition === 'hidden') {
      hidden.push(record);
      continue;
    }
    if (decision.disposition === 'redact-fields') {
      redactedFields += 1;
    }
    visible.push(record);
  }

  const summary: RecordVisibilityFilterSummary = {
    total: records.length,
    visible: visible.length,
    hidden: hidden.length,
    redactedFields,
  };
  viewerCtx.onVisibilityFilter?.(summary);
  return { records: visible, summary, hiddenRecords: hidden };
}

export function normalizeRecordSensitivity(
  universalProps: Record<string, unknown>,
): FieldSensitivity {
  const rawTier = readSensitivityTier(universalProps);
  if (rawTier == null) return 'internal';
  return normalizeTierValue(rawTier, 'sensitive_pii');
}

export function normalizeRecordSensitivityTier(
  universalProps: Record<string, unknown>,
): RecordSensitivityTier {
  const rawTier = readSensitivityTier(universalProps);
  if (rawTier == null) return 'internal';
  return normalizeRecordTierValue(rawTier, 'internal');
}

export function isRecordSensitivityTier(value: unknown): value is RecordSensitivityTier {
  return typeof value === 'string' &&
    RECORD_TIERS.has(value.trim().toLowerCase() as RecordSensitivityTier);
}

function normalizeViewerTierCeiling(
  ceiling: ViewerTierCeiling | null | undefined,
): FieldSensitivity | 'all' {
  if (ceiling == null || ceiling === 'all') return 'all';
  return normalizeTierValue(ceiling, 'public');
}

function normalizeTierValue(value: unknown, unknownFallback: FieldSensitivity): FieldSensitivity {
  if (typeof value !== 'string') return unknownFallback;
  const normalized = value.trim().toLowerCase();
  if (FIELD_SENSITIVITIES.has(normalized as FieldSensitivity)) {
    return normalized as FieldSensitivity;
  }
  if (RECORD_TIERS.has(normalized as RecordSensitivityTier)) {
    return RECORD_TIER_TO_FIELD_SENSITIVITY[normalized as RecordSensitivityTier];
  }
  return unknownFallback;
}

function normalizeRecordTierValue(
  value: unknown,
  unknownFallback: RecordSensitivityTier,
): RecordSensitivityTier {
  if (typeof value !== 'string') return unknownFallback;
  const normalized = value.trim().toLowerCase();
  if (RECORD_TIERS.has(normalized as RecordSensitivityTier)) {
    return normalized as RecordSensitivityTier;
  }
  if (normalized === 'public') return 'public';
  if (normalized === 'internal') return 'internal';
  if (normalized === 'manager' || normalized === 'hr_restricted') return 'restricted';
  if (normalized === 'hr_confidential' || normalized === 'sensitive_pii') return 'confidential';
  return unknownFallback;
}

function readSensitivityTier(universalProps: Record<string, unknown>): unknown {
  if ('sensitivityTier' in universalProps) return universalProps.sensitivityTier;
  const sensitivity = universalProps.sensitivity;
  if (
    sensitivity &&
    typeof sensitivity === 'object' &&
    !Array.isArray(sensitivity) &&
    'tier' in sensitivity
  ) {
    return (sensitivity as { tier?: unknown }).tier;
  }
  return undefined;
}

function isRetiredRecord(universalProps: Record<string, unknown>): boolean {
  const lifecycle = universalProps.lifecycle;
  return !!(
    lifecycle &&
    typeof lifecycle === 'object' &&
    !Array.isArray(lifecycle) &&
    (lifecycle as { state?: unknown }).state === 'retired'
  );
}

function sensitivityRank(level: FieldSensitivity): number {
  const index = FIELD_SENSITIVITY_ORDER.indexOf(level);
  return index >= 0 ? index : FIELD_SENSITIVITY_ORDER.length - 1;
}
